import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { CreditsProvider } from "@/contexts/CreditsContext";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import DashboardPage from "@/pages/DashboardPage";
import WorkspacePage from "@/pages/WorkspacePage";
import MagicModePage from "@/pages/MagicModePage";
import CloneModePage from "@/pages/CloneModePage";
import TemplatesPage from "@/pages/TemplatesPage";
import TemplateEditorPage from "@/pages/TemplateEditorPage";
import TemplateDetailPage from "@/pages/TemplateDetailPage";
import BillingPage from "@/pages/BillingPage";
import AccountSettingsPage from "@/pages/AccountSettingsPage";
import DomainsPage from "@/pages/DomainsPage";
import GoogleCallbackPage from "@/pages/GoogleCallbackPage";
import OnboardingPage from "@/pages/OnboardingPage";
import OrbisSpinner from "@/components/ui/orbis-spinner";
import ConnectionStatus from "@/components/ui/ConnectionStatus";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--orbis-bg)' }}>
        <OrbisSpinner size={64} />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--orbis-bg)' }}>
        <OrbisSpinner size={64} />
      </div>
    );
  }

  if (user) {
    // Keyed by user ID so each account tracks onboarding independently
    const onboardingDone = localStorage.getItem(`orbis_onboarding_done_${user.id}`);
    return <Navigate to={onboardingDone ? "/dashboard" : "/onboarding"} replace />;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <CreditsProvider>
      <div className="min-h-screen">
        <ConnectionStatus />
        <Toaster position="top-right" />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
            <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
            <Route path="/reset-password" element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path="/workspace/:projectId" element={<ProtectedRoute><WorkspacePage /></ProtectedRoute>} />
            <Route path="/magic/:projectId" element={<ProtectedRoute><MagicModePage /></ProtectedRoute>} />
            <Route path="/clone/:projectId" element={<ProtectedRoute><CloneModePage /></ProtectedRoute>} />
            <Route path="/templates" element={<ProtectedRoute><TemplatesPage /></ProtectedRoute>} />
            <Route path="/templates/:templateSlug" element={<ProtectedRoute><TemplateDetailPage /></ProtectedRoute>} />
            <Route path="/templates/:templateId/edit" element={<ProtectedRoute><TemplateEditorPage /></ProtectedRoute>} />
            <Route path="/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><AccountSettingsPage /></ProtectedRoute>} />
            <Route path="/domains" element={<ProtectedRoute><DomainsPage /></ProtectedRoute>} />
            <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
            {/* Google OAuth callback — must be unprotected, handles token on arrival */}
            <Route path="/auth/callback" element={<GoogleCallbackPage />} />
          </Routes>
        </BrowserRouter>
      </div>
      </CreditsProvider>
    </AuthProvider>
  );
}

export default App;
